/**
 * Lumi cart: add/remove toggles for the drawer's pair-offer upsell and gift box.
 *
 * All state lives server-side. Every mutation goes through Shopify's cart AJAX
 * routes with the Section Rendering API, then the drawer re-renders from the
 * response — so button states, savings, the shipping bar, and the pair offer
 * are always derived from the real cart. Using /cart/add and /cart/update also
 * emits Shopify's standard cart customer events, which the Meta/TikTok/Google
 * channel pixels consume with no extra theme code.
 *
 * Toggle modes (data-lumi-toggle):
 *   add    → add one variant (data-variant-id), data-quantity times (default 1). Buddies, gift box, tool packs.
 *   pair   → add BOTH kit variants in one call (data-variant-ids="id1,id2").
 *   remove → set one or more line keys to qty 0 (data-line-key="key1,key2").
 */
(function () {
  let busy = false;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-lumi-toggle]');
    if (!btn || busy) return;
    event.preventDefault();

    const drawer = document.querySelector('cart-drawer');
    const mode = btn.dataset.lumiToggle;

    busy = true;
    btn.disabled = true;
    btn.classList.add('lumi-btn--loading');

    const sections = drawer
      ? drawer.getSectionsToRender().map((s) => s.id)
      : ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    const config = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    };

    let url;
    if (mode === 'add' || mode === 'pair') {
      url = `${routes.cart_add_url}.js`;
      let ids;
      if (mode === 'pair') {
        ids = (btn.dataset.variantIds || '')
          .split(',')
          .map((s) => Number(s.trim()))
          .filter(Boolean);
      } else {
        ids = [Number(btn.dataset.variantId)];
      }
      config.body = JSON.stringify({
        items: ids.map((id) => ({ id, quantity: Number(btn.dataset.quantity) || 1 })),
        sections,
        sections_url: sectionsUrl,
      });
    } else {
      // remove — support one or many line keys
      url = `${routes.cart_update_url}.js`;
      const updates = {};
      (btn.dataset.lineKey || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((key) => {
          updates[key] = 0;
        });
      config.body = JSON.stringify({ updates, sections, sections_url: sectionsUrl });
    }

    fetch(url, config)
      .then((response) => response.json())
      .then((state) => {
        if (state.status && state.status !== 200) throw new Error(state.description || 'Cart error');
        if (drawer) {
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }
        // /cart/add.js answers with only the added lines, so subscribers that read cartData as the whole cart
        // (the box builder's gift reconcile) get the full cart instead.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((cart) => publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lumi-cart', cartData: cart }));
        }
      })
      .catch((error) => {
        console.error('[lumi-cart]', error);
        btn.disabled = false;
      })
      .finally(() => {
        busy = false;
        btn.classList.remove('lumi-btn--loading');
      });
  });
})();

/**
 * Scroll-hint arrow — same contract as the reference #cart-scroll-arrow:
 *   show:  on every drawer open / cart re-render, one rAF after layout, only
 *          if the scroll body overflows by more than 16px.
 *   hide:  once the user scrolls within 12px of the bottom — opacity fades
 *          over the CSS 0.35s transition, then display:none; latched until
 *          the next reset.
 * cart-drawer.js calls window.lumiCartArrow.reset() from open() and
 * renderContents(). The drawer subtree is replaced on every Section Rendering
 * re-render, so the scroll listener lives on document (capture — scroll
 * doesn't bubble) instead of on the element.
 */
(function () {
  let done = false;

  const arrowEl = () => document.querySelector('#CartDrawer .lumi-scroll-arrow');
  const scrollEl = () => document.querySelector('#CartDrawer .lumi-scroll');

  function hide() {
    const arrow = arrowEl();
    if (!arrow) return;
    done = true;
    arrow.style.opacity = '0';
    setTimeout(() => {
      arrow.style.display = 'none';
    }, 350);
  }

  function reset() {
    const arrow = arrowEl();
    const scroller = scrollEl();
    if (!arrow || !scroller) return;
    done = false;
    arrow.style.display = 'none';
    // Defer one frame so the freshly rendered drawer has reflowed.
    requestAnimationFrame(() => {
      if (done) return;
      if (scroller.scrollHeight > scroller.clientHeight + 16) {
        arrow.style.opacity = '1';
        arrow.style.display = 'flex';
      } else {
        arrow.style.display = 'none';
      }
    });
  }

  document.addEventListener(
    'scroll',
    (event) => {
      if (done) return;
      const scroller = event.target;
      if (!(scroller instanceof Element) || !scroller.classList.contains('lumi-scroll')) return;
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 12) hide();
    },
    { capture: true, passive: true },
  );

  window.lumiCartArrow = { reset, hide };
})();

/**
 * Post-render sync. Every cart mutation replaces the drawer subtree (Section
 * Rendering API), which resets the scroll offset and wipes per-element state,
 * so after each render we: (1) restore the scroll offset the user was at
 * (reference behavior — its scroller element survived renders); (2) re-arm
 * the scroll-hint arrow; (3) fire the free-shipping celebration ONLY when the
 * cart crossed the threshold this render (never on a re-render of an
 * already-unlocked cart).
 * Runs from Dawn's cartUpdate pub/sub (covers line-item removes in cart.js)
 * and directly from cart-drawer.js open()/renderContents().
 */
(function () {
  let lastScrollTop = 0;
  let wasUnlocked = null; // null = unknown on first sync → no celebration

  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target;
      if (scroller instanceof Element && scroller.classList.contains('lumi-scroll')) {
        lastScrollTop = scroller.scrollTop;
      }
    },
    { capture: true, passive: true },
  );

  let lastKeys = null; // line keys from the previous render; null until the first one (no animation on load)
  let lastFill = null; // ladder fill width from the previous render

  function animate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // New lines and gifts pop in.
    const els = [...document.querySelectorAll('#CartDrawer [data-line-key]')];
    const keys = els.map((el) => el.dataset.lineKey);
    if (lastKeys) els.forEach((el) => { if (!lastKeys.includes(el.dataset.lineKey)) el.classList.add('is-new'); });
    lastKeys = keys;
    // The ladder fill grows (or shrinks) from where it was instead of jumping.
    const fill = document.querySelector('#CartDrawer .lc2-track__fill');
    if (fill) {
      const to = fill.style.width;
      if (lastFill !== null && lastFill !== to) {
        fill.style.transition = 'none';
        fill.style.width = lastFill;
        void fill.offsetWidth;
        fill.style.transition = '';
        // a timer, not requestAnimationFrame: rAF never fires in a background tab and the bar would stay short
        setTimeout(() => { fill.style.width = to; }, 30);
      }
      lastFill = to;
    }
  }

  function sync() {
    const scroller = document.querySelector('#CartDrawer .lumi-scroll');
    if (scroller && lastScrollTop) scroller.scrollTop = lastScrollTop;
    if (scroller) scroller.scrollLeft = 0;
    animate();

    window.lumiCartArrow?.reset();
    window.lumiBoxGifts?.check();

    const bar = document.querySelector('#CartDrawer .lc2-ship');
    const unlocked = !!(bar && bar.classList.contains('is-free'));
    if (bar && unlocked && wasUnlocked === false) {
      bar.classList.add('lc2-ship--celebrate');
      setTimeout(() => bar.classList.remove('lc2-ship--celebrate'), 1800);
    }
    wasUnlocked = unlocked;
  }

  window.lumiCartSync = sync;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => sync());
  }
})();

/**
 * Per-line remove feedback — reference swaps the button text to "Removing..."
 * while the POST is in flight (the re-render replaces the button afterwards).
 * Dawn's cart-remove-button handles the actual removal.
 */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('cart-remove-button .lumi-line__remove');
  if (!btn) return;
  btn.textContent = 'Removing...';
  // Fold the line away while Dawn's request runs; the re-render then drops it for real.
  btn.closest('.lc2-line')?.classList.add('is-removing');
});

/* The quantity pill's bin (minus at 1) removes the line: fold it away the same way. */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('#CartDrawer .lc2-qty__btn[name="minus"]');
  const input = btn && btn.parentElement.querySelector('input');
  if (input && parseInt(input.value, 10) <= 1) btn.closest('.lc2-line')?.classList.add('is-removing');
}, true);

/* Busy state on the card whose button was tapped (buddy / extra), so the tap visibly registers. */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('#CartDrawer [data-lumi-toggle]');
  if (btn) btn.closest('.lc2-buddy, .lc2-extra')?.classList.add('is-busy');
});

/**
 * Box gifts follow the box everywhere. The box builder page reconciles its own gift lines; everywhere else the
 * drawer does it, so a buddy added (or a kit removed) from the drawer still earns or drops the 6- and 12-kit
 * gifts. The drawer renders what this box has earned into [data-lumi-box] (snippets/lumi-cart-zones.liquid,
 * zone 'boxdata'); this compares it with the gift lines in the cart and fixes any difference with the same
 * line shape the builder writes. Long-distance / pack carts and the builder page are left alone.
 */
(function () {
  let running = false;
  let lastSig = '';
  let tries = 0;

  async function post(path, body) {
    const r = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + path : '/' + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('Cart error ' + r.status);
    return r.json();
  }

  async function renderDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return;
    const ids = drawer.getSectionsToRender().map((s) => s.id).join(',');
    // No Accept header here: the Section Rendering API answers an Accept: application/json request with an empty body.
    const sections = await fetch(`${window.location.pathname}?sections=${ids}`, { cache: 'no-store' }).then((r) => r.json());
    drawer.renderContents({ sections });
  }

  /* The gift box comes in two designs and follows the cart's majority world (box.box, from lumi-box-world).
     Box lines this script doesn't own (the Date Night page's free box) are swapped to the right design here;
     the tier gifts below pick it up through box.want. Box lines don't count toward the world, so a swap
     can't flip it back. */
  async function fixLooseBoxes(box, cart) {
    const b = box.box || {};
    const ids = (b.ids || []).filter(Boolean).map(Number);
    const want = Number(b.want);
    if (!want || ids.length < 2) return false;
    const wrong = cart.items.filter((l) => ids.includes(l.variant_id) && l.variant_id !== want && !(l.properties && l.properties._lumi_box_gift));
    if (!wrong.length) return false;
    await post('cart/update.js', { updates: Object.fromEntries(wrong.map((l) => [l.key, 0])) });
    await post('cart/add.js', { items: wrong.map((l) => ({ id: want, quantity: l.quantity, properties: l.properties || {} })) });
    return true;
  }

  async function check() {
    if (running || document.querySelector('[data-bb]')) return;
    const el = document.querySelector('#CartDrawer [data-lumi-box]');
    if (!el) return;
    let box;
    try { box = JSON.parse(el.textContent); } catch (e) { return; }
    if (!box.managed && !(box.box && box.box.want)) return;
    running = true;
    try {
      // What the box has earned comes from the render; what the cart holds is read live, so a drawer that
      // re-rendered from an older response can't make us remove or double up gift lines.
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (await fixLooseBoxes(box, cart)) { await renderDrawer(); return; }
      if (!box.managed) return;
      const have = cart.items.filter((l) => l.properties && l.properties._lumi_box_gift);
      if (have.some((l) => l.properties._for)) return; // long-distance gifts belong to the builder
      const want = box.want || [];
      const same = have.length === want.length &&
        want.every((g) => have.some((h) => h.variant_id === g.id && h.quantity === g.qty && String(h.properties._lumi_box_gift) === String(box.tag)));
      if (same) {
        tries = 0;
        // The cart is right but this render predates it (a late response won the race): draw it again once.
        const shown = (box.have || []).length;
        if (shown !== have.length) await renderDrawer();
        return;
      }
      // Never loop: the same mismatch gets two attempts, then it waits for the next real cart change.
      const sig = JSON.stringify([have.map((h) => h.key), want, box.tag]);
      if (sig === lastSig && ++tries > 1) return;
      if (sig !== lastSig) { lastSig = sig; tries = 0; }

      if (have.length) await post('cart/update.js', { updates: Object.fromEntries(have.map((h) => [h.key, 0])) });
      if (want.length) {
        await post('cart/add.js', {
          items: want.map((g) => ({ id: g.id, quantity: g.qty, properties: { _lumi_box_gift: String(box.tag) } })),
        });
      }
      await renderDrawer();
    } catch (e) {
      console.error('[lumi-cart] box gifts', e);
    } finally {
      running = false;
    }
  }

  window.lumiBoxGifts = { check };
})();

/**
 * Buddy strip: one direction per gesture. A trackpad swipe is rarely perfectly straight, and without this a
 * diagonal swipe over the strip moved the strip sideways AND the drawer down at the same time. The first wheel
 * events of a gesture pick the axis (whichever moved more); the rest of that gesture goes only there:
 *   vertical   -> the drawer's scroller, the strip stays put
 *   horizontal -> the strip, the drawer stays put
 * Touch screens already lock the axis natively, so this only handles wheel / trackpad input.
 */
(function () {
  let axis = null;
  let lastAt = 0;
  document.addEventListener(
    'wheel',
    (event) => {
      const strip = event.target instanceof Element && event.target.closest('#CartDrawer .lc2-buddies');
      if (!strip || event.ctrlKey) return; // ctrl+wheel = pinch zoom, leave it alone
      const now = performance.now();
      if (now - lastAt > 160) axis = null; // a pause means a new gesture
      lastAt = now;
      const dx = event.deltaX;
      // Shift+wheel on a mouse means sideways.
      const dy = event.shiftKey && !dx ? 0 : event.deltaY;
      const sx = event.shiftKey && !dx ? event.deltaY : dx;
      if (!axis) {
        if (Math.abs(sx) < 1 && Math.abs(dy) < 1) return;
        axis = Math.abs(sx) > Math.abs(dy) ? 'x' : 'y';
      }
      event.preventDefault();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? strip.clientWidth : 1;
      if (axis === 'x') {
        strip.scrollLeft += sx * unit;
      } else {
        const scroller = strip.closest('.lumi-scroll');
        if (scroller) scroller.scrollTop += dy * unit;
      }
    },
    { passive: false, capture: true },
  );
})();
